import moment from 'moment-timezone';

const appendLeadZero = (val) => (Number(val) > 9 ? val : `0${val}`);

const DAYS = ['Sun', 'Mon', 'Tues', 'Wed', 'Thur', 'Fri', 'Sat'];

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'April',
  'May', 'June', 'July', 'Aug',
  'Sept', 'Oct', 'Nov', 'Dec'
];

/**
 * recieve a date value and return true if the date is today. Otherwise, false.
 * @param {String} date
 * @returns {Boolean}
 */
export const isToday = (date) => {
  const theDate = new Date(date);
  if (theDate.toString() === 'Invalid Date') return null;
  const today = new Date();
  return today.setHours(0, 0, 0, 0) === theDate.setHours(0, 0, 0, 0);
};

/**
 * recieve a date-time string and return date
 * @param {String} dateString
 * @returns {String} Format: Tues, 24 Sept 2019
 */
export const getDate = (dateString) => {
  const date = new Date(dateString);
  if (date.toString() === 'Invalid Date') return null;

  return `${DAYS[date.getDay()]}, ${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

export const getTime = (dateString) => {
  const date = new Date(dateString);
  if (date.toString() === 'Invalid Date') return null;

  return `${date.toLocaleString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}`;
};

export const getCurrentDateTime = (dateObj) => {
  const date = new Date(dateObj);
  if (date.toString() === 'Invalid Date') return null;
  const day = isToday(date) ? 'Today'
    : `${DAYS[date.getDay()]}, ${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;

  return `${day}
  ${date.toLocaleString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}`;
};

export const formatDateforPicker = (date, format = null) => {
  const d = new Date(date);
  if (d.toString() === 'Invalid Date') return null;
  if (format) {
    return moment(date, format).format('YYYY-MM-DD');
  }
  return `${d.getFullYear()}-${appendLeadZero(d.getMonth() + 1)}-${appendLeadZero(d.getDate())}`;
};

export const formatDate = (date, format = null) => {
  const d = new Date(date);
  if (d.toString() === 'Invalid Date') return null;
  if (format) {
    return moment(date, format).format('YYYY-MM-DD');
  }
  return `${appendLeadZero(d.getDate())}/${appendLeadZero(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export const formatSimpleDate = (date) => {
  const d = new Date(date);
  if (d.toString() === 'Invalid Date') return null;
  return `${appendLeadZero(d.getDate())}/${appendLeadZero(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export const getDaysFromDate = (date) => {
  const now = moment();
  return moment(date, 'YYYY-MM-DDTHH:mm:ss.SSSZ').diff(now, 'days') + 1;
};

export const formatTimeFromNow = (date) => moment(date, 'YYYY-MM-DDTHH:mm:ss.SSSZ').fromNow();

export const formatDateTime = (date) => {
  if (!date) return null;

  if (date.match(/\d{2}\/\d{2}\/\d{4}/)) {
    return moment.tz(date, 'MM/DD/YYYY', 'Africa/Lagos').format('Do MMM YYYY, h:mm A');
  } if (date.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d+/)) {
    return moment.tz(date, 'YYYY-MM-DDTHH:mm:ss.SSSZ', 'Africa/Lagos').format('Do MMM YYYY, h:mm A');
  } if (date.match(/\d{4}-\d{2}-\d{2}/)) {
    return moment.tz(date, 'YYYY-MM-DD', 'Africa/Lagos').format('Do MMM YYYY, h:mm A');
  } return null;
};

export const getYesterdayDate = (date, format = null) => {
  const d = new Date(date.getTime() - (24 * 60 * 60 * 1000));
  if (d.toString() === 'Invalid Date') return null;
  if (format) {
    return moment(date, format).format('YYYY-MM-DD');
  }
  return `${d.getFullYear()}-${appendLeadZero(d.getMonth() + 1)}-${appendLeadZero(d.getDate())}`;
};

export const dateFormatter = (date) => {
  const newDate = new Date(date).toLocaleString('en-UK', {
    year: 'numeric',
    month: 'short', 
    day: 'numeric'
  });
  return newDate;
}
export const formatToMDY = (date) => {
  const d = new Date(date);
  if (d.toString() === "Invalid Date") return null;

  // Extract month, day, and year
  const month = (d.getMonth() + 1).toString().padStart(2, "0"); // Ensure 2-digit month
  const day = d.getDate().toString().padStart(2, "0"); // Ensure 2-digit day
  const year = d.getFullYear();

  // Return formatted date as "MM/DD/YYYY"
  return `${month}/${day}/${year}`;
};
const TIME_LABEL_MAP = { "09:00": "9am", "12:00": "12pm", "15:00": "3pm", "18:00": "6pm" };

const parseAllowedTimesEnv = (envVal) => {
  if (!envVal) return null;
  try {
    const parts = envVal.split(",").map(s => s.trim()).filter(Boolean);
    const hhmm = parts.map(s => (/^\d{2}:\d{2}$/.test(s) ? s : s.slice(0,5))).filter(s => /^\d{2}:\d{2}$/.test(s));
    return hhmm.length ? hhmm : null;
  } catch { return null; }
};
export const getAllowedScheduleTimes = () => {
  const fromEnv = parseAllowedTimesEnv(process.env.REACT_APP_SCHEDULE_TIMES);
  return fromEnv || ["09:00", "12:00", "15:00", "18:00"];
};

export const normalizeScheduleTime = (t, allowed = getAllowedScheduleTimes()) => {
  if (!t) return "";
  let s = String(t).trim();
  const lower = s.toLowerCase();
  const labelToTime = { "9am": "09:00", "12pm": "12:00", "3pm": "15:00", "6pm": "18:00" };
  if (labelToTime[lower]) s = labelToTime[lower];
  if (/^\d{2}:\d{2}(:\d{2})$/.test(s)) s = s.slice(0,5);
  return allowed.includes(s) ? s : "";
};

export const makeTimeOptions = (allowed = getAllowedScheduleTimes()) => (
  allowed.map(v => ({ value: v, label: `${TIME_LABEL_MAP[v] || v} (${v})` }))
);